import type { ProfileGear, RiderMember } from '@tunasong/schemas'
import { hasStageSetup } from './stage-setup.js'

/**
 * Where a rider member row came from (docs/ux/2026-10-16-rider-collaboration-ux.md, "Rider member rows carry
 * provenance"). Derived, never stored: the row keeps dates and who edited it; the member's live setup and the band's
 * membership decide the rest. The UI pairs each kind with an icon and a sentence, never colour alone.
 */
export type RowProvenance =
  /** "From {name}'s setup · {date}" */
  | { kind: 'from-setup'; name: string; date?: string }
  /** "Edited for this rider" · "Edited by {name}, {date}" */
  | { kind: 'edited'; editorName: string; date: string }
  /** "{name} updated their setup on {date}" */
  | { kind: 'drifted'; name: string; date?: string }
  /** "Setup not added yet" */
  | { kind: 'not-added'; name: string }
  /** "No longer in {Band}" */
  | { kind: 'departed'; bandName: string }
  /** "Doesn't perform with {Band}" */
  | { kind: 'not-performing'; bandName: string }

/** A row lists something: an input with a source, or gear with a name */
export const rowHasSetup = (member: RiderMember) =>
  Boolean(member.inputs?.some(input => input.source.trim()) || member.requirements?.some(item => item.name.trim()))

/**
 * The setup date this row is settled up to: its snapshot, or a later setup the lead reviewed and chose to keep the
 * rider version over. A row from before setup dates existed is settled up to the rider itself.
 */
export const settledAt = (member: RiderMember, riderUpdatedAt: string) => {
  const snapshot = member.setupUpdatedAt ?? riderUpdatedAt
  const reviewed = member.reviewedSetupAt
  return reviewed && reviewed > snapshot ? reviewed : snapshot
}

/** The member's setup changed after the row was settled */
export const isDrifted = (member: RiderMember, gear: ProfileGear | undefined, riderUpdatedAt: string) =>
  Boolean(gear?.updatedAt && gear.updatedAt > settledAt(member, riderUpdatedAt))

export interface ProvenanceInput {
  member: RiderMember
  /** The member's live setup, from their profile (either shape, via getProfileGear) */
  gear?: ProfileGear
  /** The band's members now (bandMemberIds) */
  memberIds: string[]
  /** Members who said they don't perform with the band */
  notPerforming: string[]
  riderUpdatedAt: string
  bandName: string
  names(userId: string): string
}

export const rowProvenance = (input: ProvenanceInput): RowProvenance => {
  const { member, gear, memberIds, notPerforming, riderUpdatedAt, names, bandName } = input
  if (!memberIds.includes(member.userId)) {
    return { kind: 'departed', bandName }
  }
  if (notPerforming.includes(member.userId)) {
    return { kind: 'not-performing', bandName }
  }
  if (member.editedAt) {
    return { kind: 'edited', editorName: names(member.editedBy ?? ''), date: member.editedAt }
  }
  const name = names(member.userId)
  const rowSetup = rowHasSetup(member)
  const profileSetup = hasStageSetup(gear)
  if (!rowSetup && !profileSetup) {
    return { kind: 'not-added', name }
  }
  // An empty row whose member has a setup now is behind, whatever the dates say
  if (isDrifted(member, gear, riderUpdatedAt) || (!rowSetup && profileSetup)) {
    return { kind: 'drifted', name, ...(gear?.updatedAt ? { date: gear.updatedAt } : {}) }
  }
  return { kind: 'from-setup', name, ...(member.setupUpdatedAt ? { date: member.setupUpdatedAt } : {}) }
}
